import { MotiView, useDynamicAnimation } from "moti";
import React from "react";
import { LayoutChangeEvent, Platform } from "react-native";
import { useSharedValue } from "react-native-reanimated";

import { Box } from "@whoosh/design-system/src/components/primitive";
import {
  AnimationDurations,
  AnimationEasings,
} from "@whoosh/design-system/src/theme/animations";

type RoundDetailsAnimationWrapperProps = {
  children?: React.JSX.Element;
  hide?: boolean;
  enterFrom?: "bottom" | "top";
} & React.ComponentProps<typeof MotiView>;

export const RoundDetailsAnimationWrapper = ({
  children,
  hide = false,
  style,
  enterFrom = "top",
  ...motiViewProps
}: RoundDetailsAnimationWrapperProps) => {
  const measuredHeight = useSharedValue(0);

  const state = useDynamicAnimation(() => {
    if (!hide) {
      return {
        height: 0,
        opacity: 1,
      };
    }
    return {
      height: 0,
      opacity: 0,
    };
  });

  const onLayoutHandler = (event: LayoutChangeEvent) => {
    const { nativeEvent } = event;
    measuredHeight.value = nativeEvent.layout.height;

    if (!hide) {
      state.animateTo({
        height: nativeEvent.layout.height,
        opacity: 1,
      });
    }
  };

  const height = hide ? 0 : Math.ceil(measuredHeight.value);

  state.animateTo({
    height,
    opacity: height ? 1 : 0,
  });

  return (
    <MotiView
      {...motiViewProps}
      state={state}
      transition={{
        height: {
          type: "timing",
          duration: AnimationDurations.fast02,
          easing: AnimationEasings.standard,
        },
        opacity: {
          type: "timing",
          duration: AnimationDurations.fast01,
          easing: AnimationEasings.standard,
        },
      }}
      style={{
        overflow:
          Platform.OS === "web" ? (hide ? "hidden" : "visible") : "scroll",
      }}
    >
      <Box onLayout={onLayoutHandler}>{children}</Box>
    </MotiView>
  );
};
